import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEOHead from "@/lib/seo/SEOHead";
import TrustProfileReview from "@/components/TrustProfileReview";
import api from "@/lib/api";
import { ArrowSquareOut, Globe, Phone, ShieldCheck, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const PANEL = {background:"rgba(255,255,255,.045)",border:"1px solid rgba(255,255,255,.1)"};
const SITE = "https://ranktrust.io";
const buyerResourceLabel=(r)=>{
  const t=String(r?.type||"").toLowerCase();
  if(t==="financing")return "Financing options";
  if(t==="estimate")return "Request an estimate";
  if(t==="scheduling")return "Schedule service";
  if(t==="warranty")return "Warranty information";
  if(t==="membership")return "Maintenance plan / membership";
  if(t==="emergency_service")return "Emergency service";
  if(t==="rebates")return "Rebates & special offers";
  if(t==="payment_options")return "Payment options";
  if(t==="service_area")return "Service area";
  return r?.label||"Learn more";
};
const canonicalReviewSources=(items=[])=>{
  const aliases={"google reviews":"google","google maps":"google","google business profile":"google","trust pilot":"trustpilot","better business bureau":"bbb","angies list":"angi"};
  const order=["google","trustpilot","birdeye","bbb","yelp","facebook","angi","houzz"];
  const byKey=new Map();
  for(const item of items||[]){
    const raw=String(item?.platform||"").trim().toLowerCase();
    const key=aliases[raw]||raw;
    if(!order.includes(key))continue;
    const current=byKey.get(key);
    if(!current || (item?.url&&!current?.url) || (item?.rating!=null&&current?.rating==null) || (item?.review_count!=null&&current?.review_count==null))byKey.set(key,item);
  }
  return order.map(key=>byKey.get(key)||{platform:key==="google"?"Google":key==="bbb"?"BBB":key.charAt(0).toUpperCase()+key.slice(1),verification_status:"verification_failed"});
};
function LinkedBusinessName({text,profile}){
  const value=String(text||"");
  const name=String(profile?.name||"");
  if(!value||!name||!profile?.website)return value;
  const at=value.toLowerCase().indexOf(name.toLowerCase());
  if(at<0)return value;
  return <>{value.slice(0,at)}<a href={profile.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-[#2D5BFF] hover:underline">{value.slice(at,at+name.length)}</a>{value.slice(at+name.length)}</>;
}

export default function PublicClientProfile() {
  const { slug } = useParams();
  const [profile,setProfile] = useState(null);
  const [error,setError] = useState("");
  useEffect(()=>{
    let cancelled=false;
    setProfile(null);setError("");
    api.get("/public/business-profiles/"+encodeURIComponent(slug))
      .then(({data})=>{if(!cancelled)setProfile(data);})
      .catch(()=>{if(!cancelled)setError("This business profile is not currently published.");});
    return ()=>{cancelled=true};
  },[slug]);
  if(error)return <main className="min-h-screen bg-[#050816] text-white p-8"><SEOHead path="/business" title="Business profile unavailable | RankTrust" robots="noindex,follow" indexable={false}/><div className="max-w-xl mx-auto mt-20 p-8 rounded-2xl border border-white/10 bg-white/5"><h1 className="text-3xl font-black">Profile unavailable</h1><p className="mt-3 text-slate-300">{error}</p><Link className="inline-block mt-6 text-[#2D5BFF]" to="/">Visit RankTrust →</Link></div></main>;
  if(!profile)return <main className="min-h-screen bg-[#050816] text-slate-300 p-8"><div className="max-w-4xl mx-auto mt-20" role="status">Loading business profile…</div></main>;
  const canonical=SITE+"/reviews/"+profile.slug;
  const location=[profile.city,profile.state].filter(Boolean).join(", ");
  const title=`${profile.name} Reviews${profile.city ? " in "+profile.city : ""} | RankTrust Reviews`;
  const description=(`Read RankTrust Reviews, verified business information, services and editorial details for ${profile.name}${location ? " in "+location : ""}.`).slice(0,160);
  const schema={
    "@context":"https://schema.org",
    "@type":profile.schema_type||"LocalBusiness",
    "@id":canonical+"#business",
    name:profile.name,
    url:profile.website||undefined,
    telephone:profile.phone||undefined,
    description:profile.description||undefined,
    address:location?{"@type":"PostalAddress",addressLocality:profile.city||undefined,addressRegion:profile.state||undefined,addressCountry:"US"}:undefined,
    aggregateRating:profile.aggregate_rating?{
      "@type":"AggregateRating",
      ratingValue:profile.aggregate_rating.ratingValue,
      reviewCount:profile.aggregate_rating.reviewCount,
      bestRating:5,
      worstRating:1
    }:undefined
  };
  return <div className="min-h-screen bg-zinc-50 text-zinc-900" style={{fontFamily:"Manrope,system-ui,sans-serif"}}>
    <SEOHead path={"/reviews/"+profile.slug} canonicalPath={"/reviews/"+profile.slug} title={title} description={description}/>
    <Helmet><script type="application/ld+json">{JSON.stringify(schema)}</script></Helmet>
    <header className="bg-white border-b border-zinc-200"><div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between gap-4"><Link to="/"><img alt="RankTrust" src="/ranktrust-logo.png?v=5" className="h-9 w-auto"/></Link></div></header>
    <main className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
      <nav className="text-sm text-zinc-500 mb-6"><Link to="/" className="hover:text-zinc-900">RankTrust</Link><span className="mx-2">/</span><Link to="/reviews" className="hover:text-zinc-900">RankTrust Reviews</Link><span className="mx-2">/</span>{profile.name}</nav>
      <TrustProfileReview profile={profile}/>
    </main>
    <footer className="border-t border-zinc-200 bg-white py-8"><div className="max-w-6xl mx-auto px-5 sm:px-8 text-sm text-zinc-500 flex flex-wrap justify-between gap-4"><span>RankTrust Reviews · Editorially reviewed business information and customer feedback</span><Link to="/" className="hover:text-zinc-900">About RankTrust</Link></div></footer>
  </div>;
}
